/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { useEffect, useState } from 'react'
import { type postError, type lookup, NewEnrollmentStatus, ProgramVerificationStatus, type EnrollmentUpdateInfo, type EnrollmentUpdateResponse } from 'core/types'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { CircularProgress, Typography, Grid, Box, type SelectChangeEvent } from '@mui/material'
import S3Image from 'Components/S3Image'
import { ProgramVerificationChip } from 'Components/Program/ProgramVerificationChip'
import { SelectWithLabel } from 'Components/SelectWithLabel'
import { ProgramTracker } from 'Components/Enrollment/ProgramTracker'
import { CheckBoxWithLabel } from 'Components/CheckBox'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { EnrollmentChip } from 'Components/Enrollment/EnrollmentChip'
import AlertMessage from 'Components/AlertMessage'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { AcceptDenyModal } from 'Components/Modal/AcceptDenyModal'
import { toast } from 'react-toastify'

export interface AcceptEnrollmentProps {
  enrollmentID: number
  open: boolean
  onClose: (accepted: boolean) => void
}

enum AcceptanceMode {
  NotVerified = 0,
  SessionFull = 1,
  CanEnroll = 2
}

export function AcceptEnrollment (props: AcceptEnrollmentProps): JSX.Element {
  const initialResponse: EnrollmentUpdateResponse = {
    enrollmentID: props.enrollmentID,
    facilityID: null,
    sessionID: null,
    deliveryScheduleID: null,
    waitlistImmediately: false,
    waitlistStartDate: null,
    enrollImmediately: false,
    enrollmentDate: null,
    startDate: null
  }
  const [data, setData] = useState<EnrollmentUpdateInfo | null>(null)
  const [acceptance, setAcceptance] = useState<EnrollmentUpdateResponse>(initialResponse)
  const [sessionList, setSessionList] = useState<lookup[]>([])
  const [acceptanceMode, setAcceptanceMode] = useState<AcceptanceMode | null>(null)
  const [newStatus, setNewStatus] = useState<NewEnrollmentStatus>(NewEnrollmentStatus.Processing)

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response } = await sendGet(`/NewEnrollment/UpdateDetails?id=${props.enrollmentID}`, {})
      const record = response as EnrollmentUpdateInfo
      setData(record)
      if (record.verificationStatus !== ProgramVerificationStatus.Verified) {
        setAcceptanceMode(AcceptanceMode.NotVerified)
      }
      setAcceptance({ ...acceptance, facilityID: record.facilityID, sessionID: record.sessionID, deliveryScheduleID: record.deliveryScheduleID })
    }
    void loadData()
  }, [props.enrollmentID])

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const response = await sendGet(`/SessionList/GetSessionsByTimeFrame?timeFrameID=${data?.timeFrameID ?? ''}&facilityID=${acceptance.facilityID ?? 0}`, {})
      const blank: lookup = { id: -1, name: '(None)' }
      setSessionList([blank, ...response.response])
    }
    void loadData()
  }, [acceptance.facilityID])

  const availabilityCalculated = (noCapacityLimit: boolean, enrolled: number, capacity: number): void => {
    if (data?.verificationStatus === ProgramVerificationStatus.Verified) {
      if (!noCapacityLimit && enrolled >= capacity) {
        setAcceptanceMode(AcceptanceMode.SessionFull)
      } else {
        setAcceptanceMode(AcceptanceMode.CanEnroll)
      }
    }
  }

  const disclaimerMessage = (): string => {
    if (newStatus === NewEnrollmentStatus.Waitlisted) {
      return 'The selected session does not have any seats available for the enrolling program.  This enrollment cannot be marked as Enrolled and will be placed on the wait list Instead.'
    } else if (newStatus === NewEnrollmentStatus.Enrolled || newStatus === NewEnrollmentStatus.Started) {
      return 'By Enrolling this child, they will immediately take up a seat in the selected session.'
    } else if (newStatus === NewEnrollmentStatus.Processing) {
      return 'This child will require additional steps before their enrollment can be confirmed.  By accepting you are acknowledging that your agency would like to place this child and you will take the necessary steps to do so.'
    } else {
      return ''
    }
  }

  const handleApprove = async (): Promise<void> => {
    if (!(data?.isValid ?? false)) {
      toast.error('This child is missing location or caregiver information and can not be processed.')
      return
    }
    if (acceptance.facilityID == null) {
      toast.error('Please select a Facility.')
    } else {
      const { response: rsp } = await sendPost('/NewEnrollment/AcceptEnrollment', acceptance)
      const result: { newRecordID: number, errors: postError[], success: boolean } = rsp
      if (result.success) {
        if (newStatus === NewEnrollmentStatus.Processing) {
          toast.success('The enrollment was successfully processed.')
        } else if (newStatus === NewEnrollmentStatus.Waitlisted) {
          toast.success('The enrollment was successfully wait-listed.')
        } else if (newStatus === NewEnrollmentStatus.Enrolled) {
          toast.success('The child was successfully enrolled.')
        } else if (newStatus === NewEnrollmentStatus.Started) {
          toast.success('The enrollment was successfully started.')
        }
        if (props.onClose != null) {
          props.onClose(true)
        }
      } else {
        const errors: Array<{ error: string }> = result.errors
        errors.forEach(e => {
          toast.error(e.error)
        })
      }
    }
  }

  const facilityChanged = async (e: SelectChangeEvent<number>): Promise<void> => {
    setAcceptance({ ...acceptance, facilityID: Number(e.target.value), sessionID: null })
  }

  if (data == null) {
    return <CircularProgress />
  } else {
    const dialogContent = <>
      <Typography variant='h5' >{data.serviceProviderName}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
        {data.serviceS3Key != null && <S3Image style={{ width: '30px', height: '30px' }} imageKey={data.serviceS3Key} />}
        <div>{data.serviceName}</div>
        <div><ProgramVerificationChip programName={data.programName} status={data.verificationStatus} showStatusName={true} /></div>
      </Box>
      <Grid container columns={12} spacing={1}>
        <Grid item xs={12} sm={12}>
          <SelectWithLabel
            name='facilityID'
            label='Facility'
            value={acceptance.facilityID ?? undefined}
            options={data.facilityList}
            width='100%'
            onChange={facilityChanged}
          />
        </Grid>
        <Grid item xs={6} sm={6}>
          <SelectWithLabel
            name='sessionID'
            label='Session'
            value={acceptance.sessionID ?? -1}
            options={sessionList}
            width='100%'
            onChange={(e) => {
              if (e.target.value !== null) {
                setAcceptance({ ...acceptance, sessionID: Number(e.target.value) })
              }
            }}
          />
        </Grid>
        <Grid item xs={6} sm={6}>
          <SelectWithLabel
            name='deliveryScheduleID'
            label='Delivery Schedule'
            includeNoneOption
            noneOptionText='(none)'
            value={acceptance.deliveryScheduleID ?? undefined}
            options={data.deliveryScheduleList}
            width='100%'
            onChange={(e) => {
              if (e.target.value !== null) {
                setAcceptance({ ...acceptance, deliveryScheduleID: Number(e.target.value) })
              }
            }}
          />
        </Grid>
        {acceptance.sessionID != null && <Grid item xs={12} sm={12}>
          <ProgramTracker programId={data.isdSpProgramID} sessionId={acceptance.sessionID} onDisplay={availabilityCalculated} />
        </Grid>
        }
        {acceptanceMode === AcceptanceMode.SessionFull && <>
          <Grid item xs={6} sm={6}>
            <CheckBoxWithLabel
              edge='start'
              label='Wait-list Immediately'
              dataTestId="waitlistImmediately"
              name='waitlistImmediately'
              setValue={(checked) => {
                setAcceptance({ ...acceptance, waitlistImmediately: checked })
                if (checked) {
                  setNewStatus(NewEnrollmentStatus.Waitlisted)
                } else {
                  setNewStatus(NewEnrollmentStatus.Processing)
                }
              }}
              value={acceptance.waitlistImmediately}
            />
          </Grid>
          <Grid item xs={6} sm={6}>
            <DatePickerWithLabel
              value={acceptance.waitlistStartDate}
              label='Wait-listed Start Date'
              name={'waitlistDate'}
              disabled={!acceptance.waitlistImmediately}
              onChange={(newValue: Date | null) => {
                setAcceptance({ ...acceptance, waitlistStartDate: newValue })
              }}
            />
          </Grid>
        </>}
        {acceptanceMode === AcceptanceMode.CanEnroll && <>
          <Grid item xs={6} sm={6}>
            <CheckBoxWithLabel
              edge='start'
              label='Enroll Immediately'
              dataTestId="enrollImmediately"
              name='enrollImmediately'
              setValue={(checked) => {
                setAcceptance({ ...acceptance, enrollImmediately: checked, enrollmentDate: (checked ? new Date() : null) })
                if (checked) {
                  setNewStatus(NewEnrollmentStatus.Enrolled)
                } else {
                  setNewStatus(NewEnrollmentStatus.Processing)
                }
              }}
              value={acceptance.enrollImmediately}
            />
          </Grid>
          <Grid item xs={6} sm={6}>
            <DatePickerWithLabel
              value={acceptance.enrollmentDate}
              label='Enrolled Date'
              name='enrollmentDate'
              disabled={!acceptance.enrollImmediately}
              onChange={(newValue: Date | null) => {
                setAcceptance({ ...acceptance, enrollmentDate: newValue })
              }}
            />
          </Grid>
        </>}
        {newStatus != null && <Grid item xs={12} sm={12}>
          <Grid container flexDirection='row' spacing={2}>
            <Grid item>New Enrollment Status</Grid>
            <Grid item><EnrollmentChip statusId={newStatus} /></Grid>
          </Grid>
        </Grid>
        }

        <Grid item xs={12} sm={12}>
          <AlertMessage type={'warning'} icon={<ErrorOutlineIcon />} message={disclaimerMessage()} />
        </Grid>
      </Grid>
    </>

    return <AcceptDenyModal
      open={props.open}
      title='Accept Enrollment'
      onAccept={handleApprove}
      acceptButtonText='Accept Enrollment'
      acceptbuttoncolor='primary'
      onClose={() => {
        props.onClose(false)
      }}
      dialogContent={dialogContent}
    />
  }
}
